// CreatePropertyStatus.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createPropertyStatus,
  getAllPropertyStatuses,
  getPropertyStatusByID,
  updatePropertyStatus,
} from "./services/propertyStatus.service";
import { clearPropertyStatusDetails } from "./propertyStatusSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";

const CreatePropertyStatus: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const PropertyStatusID = useAppSelector((state) => state.drawer.data);
  const PropertyStatusDetail = useAppSelector(
    (state) => state.propertyStatus.PropertyStatusDetail,
  );
  const loading = useAppSelector((state) => state.propertyStatus.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PropertyStatusDetail]);

  useEffect(() => {
    if (PropertyStatusID) {
      dispatch(getPropertyStatusByID(PropertyStatusID));
    }
    return () => {
      dispatch(clearPropertyStatusDetails());
    };
  }, [PropertyStatusID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (PropertyStatusID) {
        const editPayload = {
          ...formData,
          ID: PropertyStatusID,
        };

        const response = await dispatch(updatePropertyStatus(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllPropertyStatuses());
          dispatch(clearPropertyStatusDetails());
        }
      } else {
        const response = await dispatch(createPropertyStatus(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllPropertyStatuses());
          dispatch(clearPropertyStatusDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={PropertyStatusDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {PropertyStatusID
                      ? "Update Property Status"
                      : "Add Property Status"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Property Status Title"
                    maxLength="100"
                    placeholder="Enter Property Status title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={PropertyStatusID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreatePropertyStatus;
